import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const pub = process.env.PUBLIC_URL;

export default function LightingInstallation() {
  useEffect(() => {
    if (window.AOS) window.AOS.refresh();
  }, []);

  const lightingTypes = [
    {
      img: `${pub}/ambient.jpg`,
      title: "Ambient Lighting",
      desc: "General illumination to create a comfortable atmosphere.",
    },
    {
      img: `${pub}/task.jpg`,
      title: "Task Lighting",
      desc: "Directed light for workspaces, kitchens, and reading areas.",
    },
    {
      img: `${pub}/accent.jpg`,
      title: "Accent Lighting",
      desc: "Draw attention to artwork, architecture, or objects.",
    },
    {
      img: `${pub}/smart.jpg`,
      title: "Smart Lighting",
      desc: "App/voice/schedule control for convenience & efficiency.",
    },
  ];

  const faqs = [
    {
      q: "What are the benefits of LED lighting?",
      a: "LEDs are efficient, long-lasting, high-quality and mercury-free.",
    },
    {
      q: "Can you help with lighting design?",
      a: "Yes—our team designs layouts that balance function and style.",
    },
    {
      q: "What is smart lighting?",
      a: "Systems controlled via app, voice or automation for savings & comfort.",
    },
  ];

  return (
    <main>
      {/* Breadcrumb */}
      <section className="bg-bglight text-center px-6 py-10" data-aos="fade-up">
        <h1 className="text-3xl md:text-4xl font-extrabold text-dark">Lighting Installation Services</h1>
        <p className="mt-2 text-gray-600">
          <Link to="/" className="text-primary hover:underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/services" className="text-primary hover:underline">Services</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span>Lighting Installation</span>
        </p>
      </section>

      {/* Hero */}
      <section
        className="relative bg-center bg-cover text-white text-center px-6 py-24"
        data-aos="fade-up"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,.6),rgba(0,0,0,.6)), url('${pub}/lighting-hero.jpg')` }}
      >
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-extrabold">Illuminate Your Space with Experts</h2>
          <p className="mt-3 text-lg opacity-90">
            Residential, commercial and industrial lighting design & installation.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a href="#lighting-types" className="btn">Explore Lighting Types</a>
            <Link to="/contact" className="btn-outline">Request Consultation</Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="px-6 md:px-10 py-12 bg-bglight" data-aos="fade-up">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2">
          <div className="rounded-xl overflow-hidden shadow-md" data-aos="fade-right" data-aos-delay="100">
            <img src={`${pub}/LPG consumption customers8.jpg`} alt="Modern Lighting" className="w-full h-full object-cover" />
          </div>
          <div className="flex flex-col justify-center" data-aos="fade-left" data-aos-delay="200">
            <h3 className="text-2xl md:text-3xl font-bold text-dark">Transform Your Environment with Light</h3>
            <p className="mt-4 text-gray-700">
              We blend aesthetic design with efficient technology and precise installation—fully compliant and safe.
            </p>
            <ul className="mt-6 space-y-2">
              {[
                "Interior & exterior lighting design",
                "LED upgrades & energy efficiency",
                "Smart lighting & control systems",
                "Security & landscape lighting",
                "Commercial/industrial high-bay lighting",
                "Emergency & exit lighting"
              ].map((li) => (
                <li key={li} className="flex items-start gap-2">
                  <i className="fas fa-check-circle text-primary mt-1" />
                  <span>{li}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn mt-6">Discuss Your Project</Link>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="lighting-types" className="px-6 md:px-10 py-14" data-aos="fade-up">
        <h4 className="section-title">Lighting Solutions We Offer</h4>
        <p className="section-description">A wide range to suit every need and style.</p>
        <div className="mx-auto mt-10 grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {lightingTypes.map((item) => (
            <article key={item.title} className="card overflow-hidden">
              <div className="h-48 overflow-hidden">
                <img src={item.img} alt={item.title} className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
              </div>
              <div className="p-5">
                <h5 className="text-lg font-semibold">{item.title}</h5>
                <p className="mt-2 text-gray-600">{item.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ 
      <section className="px-6 md:px-10 py-14 bg-bglight" data-aos="fade-up">
        <h4 className="section-title">Frequently Asked Questions</h4>
        <div className="mx-auto mt-8 max-w-3xl space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className="group rounded-lg bg-white p-5 shadow-md">
              <summary className="flex cursor-pointer items-center justify-between font-semibold text-dark">
                <span><i className="fas fa-question-circle text-primary mr-2" />{f.q}</span>
                <i className="fas fa-chevron-down transition group-open:rotate-180" />
              </summary>
              <p className="mt-3 text-gray-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
*/}
      {/* CTA */}
      <section className="bg-primary text-white text-center px-6 py-16" data-aos="fade-up">
        <div className="mx-auto max-w-3xl">
          <h4 className="text-3xl md:text-4xl font-extrabold">Ready to brighten your world?</h4>
          <p className="mt-3 opacity-90">Innovative, reliable lighting—done right.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a href="tel:+27824989477" className="btn-white"><i className="fas fa-phone" /> Get a Quote</a>
            <Link to="/contact" className="btn-outline border-white text-white hover:bg-white hover:text-primary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
